import {
  breakpointSizes,
  getBreakpointUp,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import EditPhoneModal from 'src/component-modal/EditPhoneModal/EditPhoneModal'
import VerifyPhoneModal from 'src/component-modal/VerifyPhoneModal/VerifyPhoneModal'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SecondaryEmailLink from 'src/components/SecondaryEmailLink/SecondaryEmailLink'
import TestId from 'src/components/TestId/TestId'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFormFields, useScrollToScrollGroupRefs } from 'src/hooks'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import { SecondaryEmailResponse, UserListModel } from 'src/models/user.models'
import { peopleMembersDetailUrl } from 'src/navigation/urls'
import {
  useSendPhoneCode,
  useVerifyPhoneCode,
} from 'src/queries/phoneVerification/domain/phoneVerification.queries'
import { userQueryKeys } from 'src/queries/queries.constants'
import { deleteSecondaryEmail } from 'src/redux/actions/data/users.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFetchingSecondaryEmailsFromState } from 'src/redux/selectors/data/secondaryEmails.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { formatPhoneNumber } from 'src/utils/string.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type EditProfileInfoFlatFields = {
  firstName?: string
  lastName?: string
}

interface EditProfileInfoFormProps {
  className?: string
  user: UserListModel
  secondaryEmails: SecondaryEmailResponse[]
  patchingUser: boolean
  patchingUserProfile: boolean
  errorDetailMap?: ErrorDetailMap<string>
  onSuccessfulSubmit: (flatFields: EditProfileInfoFlatFields) => void
  isEditing: boolean
  setIsEditing: (val: boolean) => void
}

const EditProfileInfoForm: React.FC<EditProfileInfoFormProps> = (
  props,
): React.ReactElement => {
  const fetchingSecondaryEmails = useSelector(
    selectFetchingSecondaryEmailsFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)

  const [removeSecondaryEmail, setRemoveSecondaryEmail] =
    useState<boolean>(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)

  type ChangePhoneStep = 'closed' | 'editPhone' | 'verifyPhone'
  const [changePhoneStep, setChangePhoneStep] =
    useState<ChangePhoneStep>('closed')
  const [changePhoneIsoCountry, setChangePhoneIsoCountry] = useState('US')
  const [changePhone, setChangePhone] = useState('')
  const [changePhoneSendError, setChangePhoneSendError] = useState<
    string | undefined
  >()
  const [changePhoneVerifyError, setChangePhoneVerifyError] = useState<
    string | undefined
  >()
  const [changePhoneSendSuccess, setChangePhoneSendSuccess] = useState(false)

  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()

  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const sendPhoneCode = useSendPhoneCode()
  const verifyPhoneCode = useVerifyPhoneCode()

  const changePhoneFullNumber =
    changePhoneIsoCountry && changePhone
      ? `+${getCountryCallingCode(changePhoneIsoCountry as CountryCode)}${changePhone}`
      : ''

  // --- Change-phone flow handlers ---

  const handleEditPhoneSubmit = useCallback(
    async (newIsoCountry: string, newPhone: string): Promise<void> => {
      const fullPhone = `+${getCountryCallingCode(newIsoCountry as CountryCode)}${newPhone}`
      try {
        await sendPhoneCode.mutateAsync({ phone: fullPhone })
        setChangePhoneIsoCountry(newIsoCountry)
        setChangePhone(newPhone)
        setChangePhoneSendError(undefined)
        setChangePhoneStep('verifyPhone')
      } catch {
        setChangePhoneSendError(
          translate('Failed to send verification code. Please try again.'),
        )
      }
    },
    [sendPhoneCode, translate],
  )

  const handleVerifyPhoneCode = useCallback(
    async (code: string): Promise<void> => {
      setChangePhoneVerifyError(undefined)
      try {
        await verifyPhoneCode.mutateAsync({ code })
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        setChangePhoneStep('closed')
      } catch {
        setChangePhoneVerifyError(
          translate('Invalid verification code. Please try again.'),
        )
      }
    },
    [verifyPhoneCode, queryClient, translate],
  )

  const handleResendCode = useCallback(async (): Promise<void> => {
    setChangePhoneSendError(undefined)
    setChangePhoneSendSuccess(false)
    try {
      await sendPhoneCode.mutateAsync({ phone: changePhoneFullNumber })
      setChangePhoneSendSuccess(true)
    } catch {
      setChangePhoneSendError(
        translate('Unable to send a new code. Please try again.'),
      )
    }
  }, [sendPhoneCode, changePhoneFullNumber, translate])

  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
    topmostScrollGroup: flatFieldsTopmostScrollGroup,
  } = useFormFields<EditProfileInfoFlatFields>( // TODO: should make this string | undefined to be totally transparent
    {},
    () => ({
      firstName: [stringIsEmpty()],
      lastName: [stringIsEmpty()],
    }),
    props.errorDetailMap,
  )
  useEffect((): void => {
    setFlatFields({
      firstName: props.user.firstName,
      lastName: props.user.lastName,
    })
  }, [props.user])

  const scrollRef0 = useRef<HTMLFormElement>(null)
  useScrollToScrollGroupRefs([scrollRef0], [flatFieldsTopmostScrollGroup])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()

    if (flatFieldsPassedValidation) {
      if (removeSecondaryEmail) {
        dispatch(deleteSecondaryEmail(props.secondaryEmails[0].email))
        setRemoveSecondaryEmail(false)
      }
      props.onSuccessfulSubmit(flatFields)
      props.setIsEditing(false)
    }
  }

  useEffect((): void => {
    if (props.patchingUser) {
      setHasSubmitted(false)
    }
  }, [props.patchingUser])

  useEffect((): void => {
    if (props.isEditing && !props.patchingUserProfile) {
      setHasSubmitted(true)
      props.setIsEditing(false)
    }
  }, [props.patchingUserProfile])

  useEffect((): void => {
    if (hasSubmitted) {
      props.setIsEditing(true)
    }
  }, [flatFieldsErrors])

  return (
    <form
      ref={scrollRef0}
      onSubmit={onSubmit}
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <FieldLabel>
        <Trns source={'Business Email'} />
      </FieldLabel>
      <HoverableLink
        to={peopleMembersDetailUrl(props.user.id)}
        underline
        ariaLabel={props.user.email}
        size={HoverableLinkSizes.Large}
      >
        {props.user.email}
      </HoverableLink>
      {props.secondaryEmails && (
        <div
          css={css`
            margin-top: 24px;
          `}
        >
          <div
            css={css`
              display: flex;
              gap: 20px;
              align-items: flex-start;
            `}
          >
            <FieldLabel>
              <Trns source={'Secondary Email'} />
            </FieldLabel>
            {props.isEditing &&
              props.secondaryEmails[0]?.email !== undefined &&
              (!removeSecondaryEmail ? (
                <HoverableLink
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )};
                    &:hover {
                      ${getFontCss(
                        14,
                        styleConstants.colors.red_dark,
                        styleConstants.fontFamilies.regular,
                      )};
                    }
                  `}
                  underline
                  regular
                  size={HoverableLinkSizes.Medium}
                  ariaLabelSource={'Remove'}
                  onClick={(): void => {
                    setRemoveSecondaryEmail(true)
                    analytics?.track('secondary user email - remove')
                  }}
                >
                  <TestId testId={'removeButton'}>
                    <Trns source={'Remove'} />
                  </TestId>
                </HoverableLink>
              ) : (
                <HoverableLink
                  underline
                  regular
                  size={HoverableLinkSizes.Medium}
                  ariaLabelSource={'Undo'}
                  onClick={(): void => {
                    setRemoveSecondaryEmail(false)
                  }}
                >
                  <Trns source={'Undo'} />
                </HoverableLink>
              ))}
          </div>
          <SecondaryEmailLink
            loading={fetchingSecondaryEmails}
            secondaryEmails={props.secondaryEmails}
            isEditing={props.isEditing}
            removeSecondaryEmail={removeSecondaryEmail}
          />
        </div>
      )}
      <div
        css={css`
          display: flex;
          margin-top: 24px;
          gap: 20px;
          align-items: flex-start;
        `}
      >
        <FieldLabel>
          <Trns source={'Phone Number'} />
        </FieldLabel>
        {props.isEditing && props.user?.phone && (
          <HoverableLink
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
              &:hover {
                ${getFontCss(
                  14,
                  styleConstants.colors.red_dark,
                  styleConstants.fontFamilies.regular,
                )};
              }
            `}
            underline
            regular
            size={HoverableLinkSizes.Medium}
            ariaLabelSource={'Edit'}
            onClick={(): void => {
              setChangePhoneStep('editPhone')
              analytics?.track('phone number - change')
            }}
          >
            <TestId testId={'removeButton'}>{translate('Edit')}</TestId>
          </HoverableLink>
        )}
      </div>
      <div
        css={css`
          display: flex;
          gap: 20px;
        `}
      >
        {props.user?.phone && (
          <span>{formatPhoneNumber(props.user?.phone)}</span>
        )}
        {!props.user.phone && (
          <div
            css={css`
              display: flex;
            `}
          >
            <TestId testId={'addPhoneNumberLink'}>
              <HoverableLink
                css={css`
                  display: flex;
                  flex-direction: row;
                `}
                onClick={(): void => {
                  setChangePhoneStep('editPhone')
                  analytics?.track('add phone - profile settings')
                }}
                underline
                regular
                size={HoverableLinkSizes.Large}
                ariaLabelSource={'Add a phone number'}
              >
                {'+ '}
                <Trns source={'Add a phone number'} />
              </HoverableLink>
            </TestId>
          </div>
        )}
        {!props.isEditing &&
          props.user?.phone &&
          !props.user?.phoneVerified && (
            <HoverableLink
              css={css`
                margin-left: -10px;
              `}
              onClick={async (): Promise<void> => {
                const userPhone = props.user.phone as string
                let parsed
                try {
                  parsed = parsePhoneNumberFromString(userPhone)
                } catch {
                  parsed = undefined
                }
                setChangePhoneSendError(undefined)
                setChangePhoneSendSuccess(false)
                sendPhoneCode
                  .mutateAsync({ phone: userPhone })
                  .then(() => {
                    if (parsed?.country) {
                      setChangePhoneIsoCountry(parsed.country)
                    }
                    setChangePhone(
                      parsed?.nationalNumber ??
                        userPhone.replace(/^\+\d{1,3}/, ''),
                    )
                    setChangePhoneStep('verifyPhone')
                  })
                  .catch(() => {
                    setChangePhoneSendError(
                      translate(
                        'Failed to send verification code. Please try again.',
                      ),
                    )
                  })
                analytics?.track('verify phone - profile settings')
              }}
              ariaLabelSource={'Verify'}
            >
              <Trns
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
                source={'Verify'}
              />
            </HoverableLink>
          )}
      </div>
      <FieldLabel
        css={css`
          margin-top: 24px;
        `}
      >
        <Trns source={'First Name'} />
      </FieldLabel>
      {props.isEditing ? (
        <TextInputField
          design={TextInputFieldDesigns.New}
          errors={flatFieldsErrors['firstName']}
          placeholder={translateSingleString(locale, 'Enter first name')}
          value={flatFields['firstName']}
          onChange={(val: string): void => onFlatFieldChange('firstName', val)}
        />
      ) : (
        <span>{flatFields['firstName']}</span>
      )}
      <FieldLabel
        css={css`
          margin-top: 24px;
        `}
      >
        <Trns source={'Last Name'} />
      </FieldLabel>
      {props.isEditing ? (
        <TextInputField
          design={TextInputFieldDesigns.New}
          errors={flatFieldsErrors['lastName']}
          placeholder={translateSingleString(locale, 'Enter last name')}
          value={flatFields['lastName']}
          onChange={(val: string): void => onFlatFieldChange('lastName', val)}
        />
      ) : (
        <span>{flatFields['lastName']}</span>
      )}
      {props.isEditing && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 40px;
            margin-top: 40px;
          `}
        >
          <TestId testId={'saveChangesButton'}>
            <Button
              css={css`
                ${getBreakpointUp(breakpointSizes.SM)(css`
                  align-self: flex-start;
                `)}
              `}
              design={ButtonDesigns.designCta}
              type={ButtonTypes.submit}
              text={<Trns source={'Save Changes'} />}
              loading={props.patchingUserProfile}
              disabled={props.patchingUser}
              big
            />
          </TestId>
          <HoverableLink
            onClick={(): void => {
              props.setIsEditing(false)
              removeSecondaryEmail && setRemoveSecondaryEmail(false)
            }}
            underline
            ariaLabelSource={'Cancel'}
          >
            <Trns source={'Cancel'} />
          </HoverableLink>
        </div>
      )}
      <EditPhoneModal
        isOpen={changePhoneStep === 'editPhone'}
        initialPhoneIsoCountry={changePhoneIsoCountry}
        initialPhone={changePhone}
        isSubmitting={sendPhoneCode.isPending}
        phoneError={changePhoneSendError}
        onSubmit={handleEditPhoneSubmit}
        onGoBack={(): void => setChangePhoneStep('closed')}
      />

      <VerifyPhoneModal
        isOpen={changePhoneStep === 'verifyPhone'}
        phoneNumber={changePhoneFullNumber}
        isVerifying={verifyPhoneCode.isPending}
        verifyError={changePhoneVerifyError}
        resendError={changePhoneSendError}
        verificationResent={changePhoneSendSuccess}
        onVerify={handleVerifyPhoneCode}
        onResend={handleResendCode}
        onEditPhone={(): void => setChangePhoneStep('editPhone')}
      />
    </form>
  )
}

export default EditProfileInfoForm
